import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useClippedText } from "@/hooks/useClippedText";

/**
 * AUT-237 (`ui/truncation-tooltips`): the clipping class is a static map, never built by
 * interpolation. Tailwind's scanner reads source text -- a constructed `line-clamp-${n}` is never
 * emitted into the stylesheet and the clamp silently does not apply.
 */
const CLAMP_CLASS: Record<1 | 2, string> = {
  1: "line-clamp-1",
  2: "line-clamp-2",
};

/**
 * `display: block` (and any other display utility) silently defeats `-webkit-box` clamping, and
 * `cn`/`twMerge` does not reliably strip it -- see
 * `decisions/truncated-text-measures-the-dom-not-its-props.md`. So a display utility arriving
 * through a caller's `className` is stripped explicitly before it ever reaches `cn` in `lines` mode.
 */
const DISPLAY_UTILITIES = ["block", "inline-block", "flex", "inline-flex", "grid", "inline-grid"];

function stripDisplayUtilities(className: string | undefined): string {
  if (!className) return "";
  return className
    .split(/\s+/)
    .filter((token) => token.length > 0 && !DISPLAY_UTILITIES.includes(token))
    .join(" ");
}

interface TruncatedTextProps {
  children: ReactNode;
  /** The untruncated value, when it differs from what is rendered (e.g. shortened in JS). */
  fullValue?: string;
  /** Clamp to n lines instead of the default single-line ellipsis. */
  lines?: 1 | 2;
  width?: number | string;
  className?: string;
}

/**
 * A synchronous, DOM-free proxy for "will this element render any visible text". Used only to
 * decide whether to mount the tooltip machinery at all -- deliberately conservative for anything
 * that is not a string/number/array (an element or fragment counts as "has content") so it never
 * has to wait for a DOM measurement to make that call. Kept prop-derived rather than reading
 * `textRef` so it never changes value mid-lifecycle purely because a resize re-measured -- that
 * would swap the render tree between a bare `<span>` and a `<Tooltip>`-wrapped one and remount the
 * element, which is exactly what FR-3's "no remount" rules out.
 */
function hasRenderableText(node: ReactNode): boolean {
  if (node == null || typeof node === "boolean") return false;
  if (typeof node === "string" || typeof node === "number") return String(node).trim() !== "";
  if (Array.isArray(node)) return node.some(hasRenderableText);
  return true;
}

export function TruncatedText({ children, fullValue, lines, width, className }: TruncatedTextProps) {
  const elementRef = useRef<HTMLSpanElement>(null);
  const textRef = useRef<string>("");
  const [isTooltipAvailable, setIsTooltipAvailable] = useState(false);

  const measure = useCallback(() => {
    const el = elementRef.current;
    if (!el) return;

    textRef.current = el.textContent ?? "";

    const isClipped = lines != null ? el.scrollHeight > el.clientHeight : el.scrollWidth > el.clientWidth;
    const hasDistinctFullValue = fullValue != null && fullValue !== textRef.current;

    setIsTooltipAvailable(hasDistinctFullValue || isClipped);
  }, [lines, fullValue]);

  useEffect(() => {
    measure();
  }, [measure, children, width]);

  useClippedText(measure);

  const style = width != null ? { width: typeof width === "number" ? `${width}px` : width } : undefined;

  const classes =
    lines != null
      ? cn(CLAMP_CLASS[lines], stripDisplayUtilities(className))
      : cn("block", "truncate", className);

  // AUT-424: the shared pass above listens for `resize` on `window`, so a container that changes
  // width without the window changing -- dragging a table column -- never re-measures, and the
  // tooltip keeps whatever verdict it got at mount. That gap is recorded as accepted in
  // `decisions/truncated-text-measures-the-dom-not-its-props.md § Consequences`; QA reported it
  // against the Keywords tab's Theme column, which cannot use the `width` prop that covers the
  // keyword column because its width is decided by table layout, not computed in JS.
  //
  // Re-measuring on pointer/focus entry closes it without paying the cost NFR-2 rejected. The ADR
  // turned down both a per-instance and a shared `ResizeObserver` because the observation set is one
  // entry per cell on exactly the screens that render the most cells. This runs once, on the single
  // element the user is actually pointing at, at the only moment the answer is needed -- the verdict
  // is read when the tooltip opens, and nothing opens a tooltip without first entering the trigger.
  // Idle cost is zero, so the registry above stays as it is rather than growing an observer.
  const element = (
    <span
      ref={elementRef}
      style={style}
      className={classes}
      data-truncated-text=""
      onPointerEnter={measure}
      onFocus={measure}
    >
      {children}
    </span>
  );

  const hasContentToShow = fullValue != null || hasRenderableText(children);

  if (!hasContentToShow) {
    return element;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{element}</TooltipTrigger>
      {isTooltipAvailable && (
        <TooltipContent side="bottom" className="max-w-[300px]">
          <p className="break-words whitespace-pre-line">{fullValue ?? textRef.current}</p>
        </TooltipContent>
      )}
    </Tooltip>
  );
}
